import { stringifyQuery } from 'vue-router'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useUserStore } from '@/stores/UserStore'
import { isOnboardingStepComplete, onboardingProgressOf } from '@/helpers/onboarding'
import { HELP_HUB_QUERY, HELP_HUB_TAB, HELP_HUB_CHAT_PAGE, HELP_HUB_TICKET_SLUG } from '@/constants'

const first = (value) => (Array.isArray(value) ? value[0] : value) || null

// The help page a route names in `?help=<slug>`, or null.
export const helpOf = (route) => first(route.query[HELP_HUB_QUERY])

// An open thread's slug names its ticket by number (`ticket-32193`): the number is what the dealer and the agent both call it, and the loaded list resolves it to the conversation.
const TICKET_SLUG = new RegExp(`^${HELP_HUB_TICKET_SLUG}-(\\d+)$`)
export const isTicketSlug = (slug) => TICKET_SLUG.test(slug ?? '')
export const ticketSlug = (number) => `${HELP_HUB_TICKET_SLUG}-${number}`
// The number a slug names, or null.
export const ticketNumberOf = (slug) => (slug ?? '').match(TICKET_SLUG)?.[1] ?? null

// The Chat tab's slugs: the tab itself, its list pages and an open thread.
export const isChatSlug = (slug) => slug === HELP_HUB_TAB.CHAT || Object.values(HELP_HUB_CHAT_PAGE).includes(slug) || isTicketSlug(slug)

// `query` without the hub's param, for pages that treat the whole query as their filters.
export const withoutHelp = (query = {}) => {
  const { [HELP_HUB_QUERY]: help, ...rest } = query
  return rest
}

// `route.fullPath` without the hub's param, for code that compares or stores the page's own URL.
export const fullPathWithoutHelp = (route) => {
  const search = stringifyQuery(withoutHelp(route.query))
  return [route.path, search && `?${search}`, route.hash].join('')
}

// True while the hub navigates to its own top level.
let closing = false

// Runs the hub's navigation to its top level, which the guard below must not undo.
export const closeHelpWith = (navigate) => {
  closing = true
  return navigate().finally(() => { closing = false })
}

// True when the browser is already at `to`: Back, Forward and the first load. A link or a redirect has not moved it yet.
export const arrivedAt = (router, to) => to.fullPath === router.options.history.location

// Everything on a step or page that can open a help page of its own: guide steps, titled lists, and text sections.
export const pagesIn = (holder) => [
  ...(holder.guide ?? []),
  ...(holder.guides ?? []).flatMap((list) => [list, ...list.steps, ...(list.sections ?? [])]),
  ...(holder.sections ?? []),
]

// The help page a route names in `meta.help`, its own or inherited: one slug, or several in order, where a finished Getting Started step gives way to the next (the last stays); `false` is the hub's main page.
export const helpOfRoute = (to) => {
  const help = to.meta.help ?? null
  if (!Array.isArray(help)) return help

  return help.find((key) => !isFinishedStep(key)) ?? help.at(-1)
}

// A Getting Started step this dealer has finished, by the same rule the steps themselves show. A slug that is not a
// step (a guide page such as `import-from-marketplace`) is never finished, so it is where a list of them ends up. The
// steps are loaded whenever the hub is open, which is the only time this decides anything.
const isFinishedStep = (key) => {
  const step = useStrapiStore().getStartedSteps.find((item) => item.key === key)

  return !!step && isOnboardingStepComplete(step, onboardingProgressOf(useUserStore().user), useGlobalStore().completedSteps)
}

// Global guard: in an open hub a route that names a help page shows it, elsewhere the open page follows the dealer; a navigation that names a page is left alone.
export const carryHelpPage = (router) => (to, from) => {
  // Back, forward and first load: the URL is already there and says what the hub shows.
  if (closing || arrivedAt(router, to) || !to.meta.requireAuth || HELP_HUB_QUERY in to.query) return

  const carry = (page) => ({ path: to.path, query: { ...to.query, [HELP_HUB_QUERY]: page }, hash: to.hash })

  // A guard that redirects answers with a bare path, which drops the page the original link named.
  const origin = to.redirectedFrom && helpOf(to.redirectedFrom)
  if (origin) return carry(origin)

  const open = helpOf(from)
  const named = helpOfRoute(to)

  // The chat and its list pages are not help pages: no route ends them.
  if (isChatSlug(open)) return carry(open)
  // A route that names the main page shows no page.
  if (named === false) return

  // Entering a route's territory shows its page; a move within it leaves the hub alone, so a page the dealer opened survives card-to-card steps.
  const entering = named && named !== helpOfRoute(from)
  const page = (useGlobalStore().helpHubOpen && entering ? named : null) || open

  return page ? carry(page) : undefined
}
